import { useState } from 'react';
import { useClient } from '../lib/ClientContext';
import { IconAI } from './icons';

/**
 * "Ask your mailbox" — natural-language Q&A over the user's own mail via the BFF
 * `/api/ai/mail` (mo2-ai mail_assist loop). Read-only; answers cite message ids
 * which open in the reading pane.
 */
export function AskAI({ onClose, onOpenMessage }: { onClose: () => void; onOpenMessage: (id: string) => void }) {
  const { session } = useClient();
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState('');
  const [citations, setCitations] = useState<string[]>([]);
  const [error, setError] = useState('');

  async function ask(e: React.FormEvent) {
    e.preventDefault();
    const question = q.trim();
    if (!question || loading) return;
    setLoading(true);
    setError('');
    setAnswer('');
    setCitations([]);
    try {
      const res = await fetch('/api/ai/mail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-zwf-csrf': session.csrfToken ?? '' },
        credentials: 'same-origin',
        body: JSON.stringify({ question }),
      });
      const data = (await res.json().catch(() => ({}))) as { answer?: string; citations?: string[]; error?: string };
      if (!res.ok || data.error) {
        setError(res.status === 429 ? 'Already answering a question — one at a time.' : 'AI request failed. Try again.');
        return;
      }
      setAnswer(data.answer ?? '');
      setCitations(Array.isArray(data.citations) ? data.citations : []);
    } catch {
      setError('AI request failed. Try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="ai-backdrop" onClick={onClose}>
      <div className="ai-panel" onClick={(e) => e.stopPropagation()}>
        <div className="ai-hdr">
          <IconAI />
          <span>Ask your mailbox</span>
          <button className="ai-x" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <form className="ai-ask" onSubmit={ask}>
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. what did JLCPCB say about my last order?"
          />
          <button className="primary" type="submit" disabled={loading || !q.trim()}>
            {loading ? '…' : 'Ask'}
          </button>
        </form>
        <div className="ai-body">
          {loading ? <div className="ai-thinking">Searching and reading your mail…</div> : null}
          {error ? <div className="ai-err">{error}</div> : null}
          {answer ? <div className="ai-answer">{answer}</div> : null}
          {citations.length ? (
            <div className="ai-cites">
              <span className="ai-cites-label">Sources</span>
              {citations.map((id) => (
                <button
                  key={id}
                  className="ai-cite"
                  onClick={() => {
                    onOpenMessage(id);
                    onClose();
                  }}
                >
                  Message {id}
                </button>
              ))}
            </div>
          ) : null}
          {!loading && !answer && !error ? (
            <div className="ai-hint">Answers use only your own mailbox — read-only and private; nothing leaves the server.</div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
